import React, { useEffect, useState, useRef } from "react";
import CustomTable from "../../components/Table";
import Header from "../../components/Header";
import { OPERTIONS_URL } from "../../common/common";
import AdminCointainer from "../../containers/Admin";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/AddButton";
import { Plus, vendor } from "../../common/svg";
import { ApiService } from "../../service/api.service";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import ToggleActive from "../../components/ToggleSwitch/ToggleActive";

type VendorData = {
  vendorId: string;
  companyName: string;
  gstNumber: string;
  address: string;
  contactDetails: string;
  isActive: boolean;
  companyType?: string;
  statusDisplay?: string;
};

type Column<T> = {
  header: string;
  dataKey: keyof T;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  align?: "left" | "center" | "right";
};

const VendorPage = () => {
  const navigate = useNavigate();
  const apiService = new ApiService();
  const [vendorData, setVendorData] = useState<VendorData[]>([]);
  const [search, setSearch] = useState<string>("");
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const entityId = localStorage.getItem('entity_id');
  const menuRef = useRef<HTMLDivElement>(null);
  // const { showToast } = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [active, setActive] = useState<boolean>(true);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggleActive = async (vendorId: string, isActive: boolean) => {
    try {
      const response: any = await apiService.putApi(
        `${OPERTIONS_URL}/vendor/status-route`,
        {
          vendorId: vendorId,
          isActive: isActive ? 1 : 0
        }
      );

      if (response.status === "200") {
        getVendorData();
        setVendorData(prevData =>
          prevData.map(vendor =>
            vendor.vendorId === vendorId ? { ...vendor, isActive } : vendor
          )
        );
      }
    } catch (error) {
      console.error("Error updating vendor status:", error);
    }
  };

  const columns: Column<VendorData>[] = [
    { header: "Company Name", dataKey: "companyName" },
    { header: "GST Number", dataKey: "gstNumber" },
    {
      header: "Company Type",
      dataKey: "companyType",
      render: (value: string | boolean | undefined) => (
        <div className="text-left max-w-[200px] whitespace-pre-wrap break-words">
          {typeof value === 'string' ? value.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          )) : null}
        </div>
      ),
    },
    {
      header: "Contact Details",
      dataKey: "contactDetails",
      render: (value: string | boolean | undefined) => (
        <div className="text-sm text-left leading-relaxed">
          {typeof value === 'string' ? value.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          )) : null}
        </div>
      ),
    },
    {
      header: "Status",
      dataKey: "statusDisplay",
      render: (value: string | boolean | undefined, row: VendorData) => {
        const status = value || (row.isActive ? "Active" : "Inactive");

        return (
          <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status === "Active"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
              }`}
          >
            {status}
          </div>
        );
      }
    },
    {
      header: "Actions",
      dataKey: "vendorId",
      align: "center",
      render: (value, row) => (
        <div
          className="relative ml-2"
          ref={activeMenu === value ? menuRef : null}
          onClick={(e) => e.stopPropagation()} // Prevent row click when interacting with menu
        >
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setActiveMenu(activeMenu === value ? null : (value as string | null));
            }}
          >
            <div className="flex flex-col items-center justify-center h-5">
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            </div>
          </button>

          {/* Dropdown Menu */}
          {activeMenu === value && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                  setActiveMenu(null);
                }}
              >
                Edit
              </button>

              <div className="px-4 py-2">
                <ToggleSwitch
                  showLabel={true}
                  checked={row.isActive}
                  onChange={(isActive) => {
                    handleToggleActive(row.vendorId as string, isActive);
                    setActiveMenu(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      ),
    }
  ];

  const handleEdit = (item: VendorData) => {
    navigate(`edit/${item.vendorId}`);
  };

  // const handleDelete = (item: ClientData) => {
  //   if (window.confirm(`Are you sure you want to delete ${item.companyName}?`)) {
  //     deleteClient(item.clientId as string);
  //   }
  // };

  // const deleteClient = async (clientId: string) => {
  //   try {
  //     const response: any = await apiService.deleteAPi(`/client/delete/${clientId}`);
  //     if (response.status === "200") {
  //       getClientData();
  //     }
  //   } catch (error) {
  //     console.error("Error deleting client:", error);
  //   }
  // };

  const getVendorData = async (isLoadingMore = false) => {
    // Only fetch if there's more data to load
    if (!hasMoreData && isLoadingMore) return;

    setLoading(true);
    try {
      const response: any = await apiService.getApi(
        `${OPERTIONS_URL}/vendor/get-all-route?page=${currentPage}&limit=${pageSize}&search=${search}&entityId=${entityId}&status=${active ? 1 : 0}`
      );


      if (response.status === "200") {
        const dataArray = response.body.data;

        const formattedData = dataArray.map((vendor: any) => {
          // Extract company type from companyTypes array
          const companyType = vendor.companyTypes && Array.isArray(vendor.companyTypes) && vendor.companyTypes.length > 0
            ? vendor.companyTypes.map((type: any) => type.value).join(", ")
            : "";

          // Format address from individual fields
          const address = `${vendor.address1 || ""} ${vendor.address2 || ""} ${vendor.city || ""} ${vendor.state || ""} ${vendor.country || ""} ${vendor.pincode || ""}`.trim();

          return {
            vendorId: vendor.vendorId,
            companyName: vendor.firstName,
            gstNumber: vendor.gstNumber,
            companyType: companyType,
            address: address,
            contactDetails: vendor.phoneNumber,
            isActive: vendor.isActive === 1,
            statusDisplay: vendor.isActive === 1 ? "Active" : "Inactive"
          };
        });


        // Determine if there's more data
        const totalRecords = parseInt(response.body.pagination.totalRecords);
        const currentLoadedItems = isLoadingMore
          ? vendorData.length + formattedData.length
          : formattedData.length;

        // Update has more data flag
        setHasMoreData(currentLoadedItems < totalRecords);

        // For infinite scroll, append new data if loading more
        setVendorData(prevData =>
          isLoadingMore ? [...prevData, ...formattedData] : formattedData
        );

        setTotalItems(totalRecords);
      } else {
        console.error('API returned non-200 status:', response.status, response.message);
        setVendorData([]);
      }
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      setHasMoreData(false);
      setVendorData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    // Only load more if there's more data
    if (hasMoreData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getVendorData(true);
    }
  };

  useEffect(() => {
    // Reset has more data and fetch initial data when search or entity changes
    setHasMoreData(true);
    setCurrentPage(1);
    getVendorData();
  }, [search, entityId, active]);

  const handleRowClick = (item: VendorData) => {
    if (active && item.isActive) {
      navigate(`view/${item.vendorId}`);
    }
  };

  const handleAdd = () => {
    navigate("add");
  };

  return (
    <div className="">
      <Header
        icon={vendor}
        title="Vendor"
        toggleComponent={
          <div className="flex items-end gap-2 ml-[450px]">
            <ToggleActive
              onChange={(isActive: boolean) => {
                setActive(isActive); // Update the state
                console.log(isActive ? "active 1" : "inactive 0");
              }}
            />
          </div>
        }
        showSearch={true}
        setSearch={setSearch}
      >
        <div>
          <CustomButton label="" onClick={handleAdd} icon={Plus} />
        </div>
      </Header>


      <AdminCointainer>
        <CustomTable<VendorData>
          columns={columns}
          data={vendorData}
          onRowClick={handleRowClick}
          loading={loading}
          onLoadMore={handleLoadMore}
        />
        {!loading && vendorData.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No vendors found. Try adjusting your search or filters.</p>
          </div>
        )}
      </AdminCointainer>
    </div>
  );
};

export default VendorPage;